import deepmerge from 'deepmerge';
import {useMemo} from 'react';
import {getIsAgeRestricted} from '../../../../common/getIsAgeRestricted';
import {t, tt} from '../../../../common/getTranslations';
import {LogTabType} from '../../../../common/LogTabType.enum';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {twMerge} from '../../../../common/twMerge';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {STATIC_LANDING_ORIGINAL_CATEGORY_LIST} from '../../../../staticLanding/original/constants';
import {useLogin} from '../../../../user/useLogin.hook';
import {IconType} from '../../../atoms/Icon/IconType.enum';
import {CheckBoxSize} from '../../../molecules/CheckBox/CheckBoxSize.enum';
import {ScrollableHorizontalList} from '../../../molecules/ScrollableHorizontalList/ScrollableHorizontalList';
import {BelowDefaultTab} from '../../../organisms/BelowDefaultTab/BelowDefaultTab';
import {CardViewItem} from '../../../organisms/CardViewItem/CardViewItem';
import {Dialog} from '../../../organisms/Dialog';
import {MainTab} from '../../../organisms/MainTab/MainTab';
import {SubTab} from '../../../organisms/SubTab/SubTab';
import {SubtabItemActiveColorScheme} from '../../../organisms/SubTabItem/constants';
import {StaticLandingOriginalProps} from './StaticLandingOriginalProps.interface';

export function StaticLandingOriginal({
  isMobile,
  isWebView,
  totalCount,
  recommendItemList,
  currentCategoryUid,
  sortTypeList,
  currentSortType,
  subcategoryList,
  currentSubcategoryUid,
  isShowSortTypeDialog,
  isShowCompleteSeries = false,
  className,
  onClickCategoryTabItem,
  onClickSortTypeTabItem,
  onClickSubcategoryItem,
  onClickCompleteOnly,
  showSortTypeDialog,
  hideSortTypeDialog,
  children,
}: StaticLandingOriginalProps) {
  const currentCategoryName = useMemo(() => {
    const currentCategory = STATIC_LANDING_ORIGINAL_CATEGORY_LIST?.find(
      item => item.id === currentCategoryUid
    );

    return tt(currentCategory);
  }, [currentCategoryUid]);
  const {user} = useLogin();

  const currentSubcategoryName = useMemo(
    () =>
      subcategoryList?.find(item => item.param === currentSubcategoryUid)?.name,
    [currentSubcategoryUid, subcategoryList]
  );
  const currentSortTypeName = useMemo(
    () => sortTypeList?.find(item => item.param === currentSortType)?.name,
    [currentSortType, sortTypeList]
  );
  const hasSortType = sortTypeList && sortTypeList.length > 0;
  return (
    <div
      className={twMerge('flex flex-col w-full mb-15pxr grow', className)}
      {...putTiaraObjectProps({
        customProps: {
          page_category_filter: currentCategoryName,
          page_genre_filter: currentSubcategoryName,
          page_sort: currentSortType,
          completed: isShowCompleteSeries ? 'y' : 'n',
        },
        click: {
          layer1: '섹션',
        },
      })}
      {...{
        [PageViewCustomPropsDataKey.Completed]: `completed=${
          isShowCompleteSeries ? 'y' : 'n'
        }`,
      }}
    >
      <TabPositionSticky
        depth={1}
        disabled={!isMobile}
        isMobile={isMobile}
        isWebView={isWebView}
      >
        <MainTab
          className={!isMobile ? 'px-15pxr' : 'p-0'}
          isMobile={isMobile}
          title={t('landing_original')}
          tabItems={STATIC_LANDING_ORIGINAL_CATEGORY_LIST}
          currentTabItemId={currentCategoryUid}
          showBorder={true}
          onClickTab={clickedTabItemId =>
            onClickCategoryTabItem &&
            onClickCategoryTabItem(clickedTabItemId as number)
          }
          logTabType={LogTabType.Category}
        />
      </TabPositionSticky>
      {recommendItemList && recommendItemList.length > 0 && (
        <ScrollableHorizontalList
          className={isMobile ? 'py-15pxr' : 'p-15pxr'}
          isMobile={isMobile}
          gapPx={4}
          innerLeftPx={isMobile ? 15 : 0}
          innerRightPx={isMobile ? 15 : 0}
        >
          {recommendItemList.map(
            ({
              id,
              thumbnail,
              badgeList,
              ageGradeBadge,
              statusBadge,
              subtitleList,
              scheme,
              ageGrade,
              selfCensorship,
              eventLog,
              categoryType,
              discountRateText,
              title,
              backgroundColor,
              isBook,
            }) => {
              const promotionCardLogObj = {
                click: {
                  layer1: '프로모션영역',
                },
              };
              return (
                <CardViewItem
                  key={id}
                  className={isMobile ? 'w-156pxr' : 'w-200pxr'}
                  thumbnail={thumbnail}
                  categoryType={categoryType}
                  badgeList={badgeList}
                  ageGradeBadge={ageGradeBadge}
                  statusBadge={statusBadge}
                  subtitleList={subtitleList}
                  scheme={scheme}
                  isAgeRestricted={getIsAgeRestricted({
                    age: user?.age,
                    authenticatedAt: user?.authenticatedAt,
                    ageGrade,
                    selfCensorship,
                  })}
                  cardViewTitle={title}
                  backgroundColor={backgroundColor}
                  isBook={isBook}
                  onClick={e => LOG_CLICK_EVENT('작품_클릭', e)}
                  {...putTiaraObjectProps(
                    eventLog
                      ? deepmerge(eventLog, promotionCardLogObj)
                      : promotionCardLogObj
                  )}
                  discountRateText={discountRateText}
                />
              );
            }
          )}
        </ScrollableHorizontalList>
      )}
      {subcategoryList && currentSubcategoryUid && (
        <SubTab
          tabItems={subcategoryList}
          currentTabItemParam={currentSubcategoryUid}
          activeColorScheme={
            !isMobile ? SubtabItemActiveColorScheme['2nd'] : undefined
          }
          onClickTab={(clickedTabItemId, e) => {
            aggregateAndSendLog({
              targetElem: e?.target as HTMLElement,
              actionName: '장르_클릭',
              actionType: 'Event',
              click: {
                copy: subcategoryList.find(
                  subcategory => subcategory.param === clickedTabItemId
                )?.name,
              },
              customProps: {
                user_action: 'click',
              },
            });
            onClickSubcategoryItem &&
              onClickSubcategoryItem(clickedTabItemId as string);
          }}
          logTabType={LogTabType.Genre}
          {...putTiaraObjectProps({
            click: {
              layer1: '장르탭',
            },
          })}
        />
      )}
      <BelowDefaultTab className="px-15pxr">
        {totalCount && (
          <BelowDefaultTab.TextLabel>
            {t('landing_series_count', {
              values: {value: formatWithCommas(totalCount)},
            })}
          </BelowDefaultTab.TextLabel>
        )}
        <BelowDefaultTab.CheckBox
          isChecked={isShowCompleteSeries}
          size={CheckBoxSize.SubTab}
          onClick={e =>
            LOG_CLICK_EVENT('완결작보기_클릭', e, onClickCompleteOnly)
          }
          {...putTiaraObjectProps({
            click: {
              layer1: '리스팅옵션',
              copy: isShowCompleteSeries ? 'N' : 'Y',
            },
          })}
        >
          {t('landing_filter_completed')}
        </BelowDefaultTab.CheckBox>
        {hasSortType && currentSortTypeName && (
          <BelowDefaultTab.SelectBox
            onClick={showSortTypeDialog}
            {...{
              [PageViewCustomPropsDataKey.PageSort]: `page_sort=${currentSortTypeName}`,
            }}
            iconType={IconType.TabFilter}
          >
            {currentSortTypeName}
          </BelowDefaultTab.SelectBox>
        )}
      </BelowDefaultTab>
      {hasSortType && (
        <Dialog
          isOpen={!!isShowSortTypeDialog}
          onClose={hideSortTypeDialog}
          isMobile={isMobile}
          {...putTiaraObjectProps({
            customProps: {
              page_category_filter: currentCategoryName,
              page_genre_filter: currentSubcategoryName,
              page_sort: currentSortType,
              completed: isShowCompleteSeries ? 'y' : 'n',
            },
          })}
        >
          <Dialog.Dimmed />
          <Dialog.List className="space-y-0">
            {sortTypeList.map(sortType => (
              <Dialog.Option
                key={sortType.param}
                onClick={e =>
                  LOG_CLICK_EVENT('정렬_클릭', e, () =>
                    onClickSortTypeTabItem(sortType.param)
                  )
                }
                isChecked={sortType.param === currentSortType}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '리스팅옵션',
                    copy: sortType.name,
                  },
                })}
              >
                {sortType.name}
              </Dialog.Option>
            ))}
          </Dialog.List>
          <Dialog.BottomCombination>
            <Dialog.Button onClick={hideSortTypeDialog}>
              {t('common_cancel_button')}
            </Dialog.Button>
          </Dialog.BottomCombination>
        </Dialog>
      )}
      {children}
    </div>
  );
}
